// ============================================================================
// BP-13-02: User Table Component
// BrightPath Platform - Admin Frontend
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/core/lib/utils';
import type { AdminUserListItem, UserStatus, UserRole } from '@/types/admin';
import { useAdminStore, selectSelectedItems } from '@/features/admin/stores/adminStore';

// =============================================================================
// Types
// =============================================================================

interface UserTableProps {
  users: AdminUserListItem[];
  isLoading?: boolean;
  onUserClick?: (userId: string) => void;
  selectable?: boolean;
}

// =============================================================================
// Status Badge
// =============================================================================

function StatusBadge({ status }: { status: UserStatus }) {
  const statusConfig: Record<UserStatus, { label: string; className: string }> = {
    active: {
      label: 'Active',
      className: 'bg-emerald-100 text-emerald-700',
    },
    suspended: {
      label: 'Suspended',
      className: 'bg-red-100 text-red-700',
    },
    pending_verification: {
      label: 'Pending',
      className: 'bg-amber-100 text-amber-700',
    },
    deactivated: {
      label: 'Deactivated',
      className: 'bg-gray-100 text-gray-600',
    },
  };

  const config = statusConfig[status];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        config.className
      )}
    >
      {config.label}
    </span>
  );
}

// =============================================================================
// Role Badge
// =============================================================================

function RoleBadge({ role }: { role: UserRole }) {
  const roleConfig: Record<UserRole, { label: string; className: string }> = {
    family_admin: {
      label: 'Family Admin',
      className: 'bg-sage/10 text-sage-dark',
    },
    family_member: {
      label: 'Family Member',
      className: 'bg-blue-100 text-blue-700',
    },
    caregiver: {
      label: 'Caregiver',
      className: 'bg-purple-100 text-purple-700',
    },
    attorney: {
      label: 'Attorney',
      className: 'bg-navy/10 text-navy',
    },
    vendor: {
      label: 'Vendor',
      className: 'bg-terracotta/10 text-terracotta',
    },
    provider: {
      label: 'Provider',
      className: 'bg-teal-100 text-teal-700',
    },
    admin: {
      label: 'Admin',
      className: 'bg-slate-100 text-slate-700',
    },
    super_admin: {
      label: 'Super Admin',
      className: 'bg-slate-800 text-white',
    },
  };

  const config = roleConfig[role];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        config.className
      )}
    >
      {config.label}
    </span>
  );
}

// =============================================================================
// Component
// =============================================================================

export function UserTable({
  users,
  isLoading,
  onUserClick,
  selectable = false,
}: UserTableProps) {
  const selectedItems = useAdminStore(selectSelectedItems);
  const toggleSelectedItem = useAdminStore((state) => state.toggleSelectedItem);
  const setSelectedItems = useAdminStore((state) => state.setSelectedItems);

  const allSelected = users.length > 0 && users.every((u) => selectedItems.includes(u.id));
  const someSelected = users.some((u) => selectedItems.includes(u.id));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedItems(selectedItems.filter((id) => !users.find((u) => u.id === id)));
    } else {
      setSelectedItems([...new Set([...selectedItems, ...users.map((u) => u.id)])]);
    }
  };

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="animate-pulse">
          <div className="h-12 bg-cream" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 border-t border-gray-100 p-4">
              <div className="h-10 w-10 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/4 rounded bg-gray-200" />
                <div className="h-3 w-1/3 rounded bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-12">
        <svg
          className="mb-4 h-12 w-12 text-warm-gray"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
          />
        </svg>
        <p className="text-warm-gray">No users found</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-cream">
          <tr>
            {selectable && (
              <th scope="col" className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={handleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-sage focus:ring-sage"
                />
              </th>
            )}
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warm-gray"
            >
              User
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warm-gray"
            >
              Role
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warm-gray"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warm-gray"
            >
              Verified
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warm-gray"
            >
              Last Login
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warm-gray"
            >
              Joined
            </th>
            <th scope="col" className="relative px-4 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {users.map((user) => {
            const isSelected = selectedItems.includes(user.id);

            return (
              <tr
                key={user.id}
                className={cn(
                  'transition-colors',
                  isSelected ? 'bg-sage/5' : 'hover:bg-cream/50'
                )}
              >
                {selectable && (
                  <td className="w-12 px-4 py-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectedItem(user.id)}
                      className="h-4 w-4 rounded border-gray-300 text-sage focus:ring-sage"
                    />
                  </td>
                )}
                <td className="whitespace-nowrap px-4 py-4">
                  <div className="flex items-center gap-3">
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sage/10">
                        <span className="text-sm font-medium text-sage-dark">
                          {user.fullName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .slice(0, 2)}
                        </span>
                      </div>
                    )}
                    <div>
                      <button
                        onClick={() => onUserClick?.(user.id)}
                        className="font-medium text-navy hover:text-sage transition-colors"
                      >
                        {user.fullName}
                      </button>
                      <p className="text-sm text-warm-gray">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-4">
                  <RoleBadge role={user.role} />
                </td>
                <td className="whitespace-nowrap px-4 py-4">
                  <StatusBadge status={user.status} />
                </td>
                <td className="whitespace-nowrap px-4 py-4">
                  {user.emailVerified ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm">Yes</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-600">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm">No</span>
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-sm text-warm-gray">
                  {user.lastLoginAt
                    ? new Date(user.lastLoginAt).toLocaleDateString()
                    : 'Never'}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-sm text-warm-gray">
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-right">
                  <Link
                    to={`/admin/users/${user.id}`}
                    className="text-sm font-medium text-sage hover:text-sage-dark transition-colors"
                  >
                    View →
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default UserTable;

